import { diagnosticLogger } from './diagnosticLogger';

export const BOOT_PAINT_REPORTED_LATCH = '__MAESTRO_BOOT_PAINT_REPORTED__';

type MaestroWindow = Window & {
  [BOOT_PAINT_REPORTED_LATCH]?: boolean;
  __TAURI__?: { core?: { invoke?: (cmd: string, args?: unknown) => Promise<unknown> } };
};

export function bootPaintAlreadyReported(win: MaestroWindow = window as MaestroWindow): boolean {
  return !!win[BOOT_PAINT_REPORTED_LATCH];
}

export function markBootPaintReported(win: MaestroWindow = window as MaestroWindow): void {
  win[BOOT_PAINT_REPORTED_LATCH] = true;
}

export function hasTauriInvoke(win: MaestroWindow = window as MaestroWindow): boolean {
  return typeof win.__TAURI__?.core?.invoke === 'function';
}

/** Milliseconds since navigation start (HTML5 `performance.now()`). */
export function bootPaintLatencyMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return Math.round(performance.now());
  }
  return 0;
}

export async function reportBootPaintEarly(
  reason = 'web_boot_early',
  path = 'main_entry',
  win: MaestroWindow = window as MaestroWindow,
): Promise<boolean> {
  if (bootPaintAlreadyReported(win)) return false;
  const invoke = win.__TAURI__?.core?.invoke;
  if (typeof invoke !== 'function') return false;

  const latencyMs = bootPaintLatencyMs();

  try {
    await invoke('webview_report_boot_paint', {
      reason,
      data: { latency_ms: latencyMs, path },
    });
  } catch {
    return false;
  }

  markBootPaintReported(win);

  try {
    diagnosticLogger.log({
      level: 'info',
      category: 'other',
      message: 'boot_paint_reported',
      payload: { latency_ms: latencyMs, path, reason },
    });
  } catch {
    /* never throw from boot logging */
  }

  return true;
}

/** Schedule the earliest module-graph paint report (backup to index.html inline). */
export function scheduleBootPaintReportEarly(): void {
  if (typeof window === 'undefined') return;
  const win = window as MaestroWindow;
  if (bootPaintAlreadyReported(win) || !hasTauriInvoke(win)) return;

  const run = () => {
    void reportBootPaintEarly('web_boot_early', 'main_rAF', win);
  };

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(run);
  } else {
    run();
  }
}
